import { stringifyLosslessJson } from '@pillar-portal/api-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    ALL_RPA_MODULE_NAMES,
    AVAILABLE_RPA_MODULE_NAMES,
    type OrganizationScopedPermissionSourceOptionsResponse,
    type OrganizationOmnisearchListItem,
    type PermissionEffect,
    PermissionSourceDesignatorType,
    type PermissionsBundlePublic,
    type PermissionsGrant,
    type PermissionSourceDesignator,
    type PermissionSourceRoleAssignmentListItem,
    IpgSystemId,
    IpgSystemIdToName,
    IpwSystemId,
    IpwSystemIdToName,
    type LogicalTerminalListItem,
    type MerchantAccountSearchListItem,
    type SecretListItem,
    resourceDesignatorKey,
    type RpaModuleName,
    type UserProfileDetailedPublicResponse,
    type TerminalDeviceListItem,
} from '@pillar-portal/api-types';
import {
    AuthorizableAction,
    ALL_RESOURCE_TYPES,
    getAllowedActionsForResourceType,
    ResourceType,
    Status,
} from '@pillar-portal/database/dist/frontend';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OmnisearchSelect, type OmnisearchFetchPage } from '@/components/ui/omnisearch-select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    organizationApi,
    paymentsInfrastructureApi,
    permissionsApi,
    secretsApi,
    userApi,
} from '@/lib/pillarApi';
import {
    getLogicalTerminalDisplayLabel,
    getTerminalDeviceDisplayLabel,
} from '@/lib/payments-infrastructure/displayLabels';
import {
    formatPermissionDesignatorLabel,
    formatPermissionResourceTypePluralLabel,
} from './PermissionGrantSummary';
import { buildUserProfileSearchFilterText } from '@/lib/user-search-filter';

const UUID_PATTERN: RegExp =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
    [ResourceType.ORGANIZATION]: 'Organization',
    [ResourceType.USER]: 'User',
    [ResourceType.ROLE_ASSIGNMENT]: 'Role Assignment',
    [ResourceType.CREDENTIAL]: 'Credential',
    [ResourceType.ORGANIZATION_CREDENTIAL]: 'Organization Credential',
    [ResourceType.ROLE_ASSIGNMENT_CREDENTIAL]: 'Role Assignment Credential',
    [ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY]: 'Organization Website Credential Policy',
    [ResourceType.IPX_CREDENTIAL_DEFINITION]: 'Website Sign-In Definition',
    [ResourceType.IPX_WEB_RESOURCE_MODEL]: 'Website Map',
    [ResourceType.ALLOWED_WEBSITE_TEMPLATE]: 'Allowed Website Template',
    [ResourceType.IPW]: 'Website',
    [ResourceType.IPG]: 'Payment Processor',
    [ResourceType.PROCESSOR]: 'Processor',
    [ResourceType.MERCHANT_ACCOUNT]: 'Merchant Account',
    [ResourceType.LOGICAL_TERMINAL]: 'Logical Terminal',
    [ResourceType.TERMINAL_DEVICE]: 'Terminal Device',
    [ResourceType.PAYMENT_TRANSACTION]: 'Payment Transaction',
    [ResourceType.PAYMENT_REQUEST]: 'Payment Request',
    [ResourceType.PAYMENT_METHOD_TOKEN]: 'Payment Method Token',
    [ResourceType.PERMISSIONS_BUNDLE]: 'Permissions Bundle',
    [ResourceType.PERMISSION_POLICY]: 'Permission Policy',
    [ResourceType.RPA_MODULE]: 'RPA Module',
};

const RESOURCE_IDENTIFIER_LABELS: Partial<Record<ResourceType, string>> = {
    [ResourceType.ORGANIZATION]: 'Organization UUID',
    [ResourceType.USER]: 'User UUID',
    [ResourceType.ROLE_ASSIGNMENT]: 'Role Assignment UUID',
    [ResourceType.CREDENTIAL]: 'Credential UUID',
    [ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY]:
        'Organization Website Credential Policy UUID',
    [ResourceType.IPX_CREDENTIAL_DEFINITION]: 'Website Sign-In Definition UUID',
    [ResourceType.IPX_WEB_RESOURCE_MODEL]: 'Website Map UUID',
    [ResourceType.ALLOWED_WEBSITE_TEMPLATE]: 'Allowed Website Template UUID',
    [ResourceType.PROCESSOR]: 'Processor UUID',
    [ResourceType.MERCHANT_ACCOUNT]: 'Merchant Account UUID',
    [ResourceType.LOGICAL_TERMINAL]: 'Logical Terminal UUID',
    [ResourceType.TERMINAL_DEVICE]: 'Terminal Device UUID',
    [ResourceType.PAYMENT_TRANSACTION]: 'Payment Transaction UUID',
    [ResourceType.PAYMENT_REQUEST]: 'Payment Request UUID',
    [ResourceType.PAYMENT_METHOD_TOKEN]: 'Payment Method Token UUID',
    [ResourceType.PERMISSIONS_BUNDLE]: 'Permissions Bundle UUID',
    [ResourceType.PERMISSION_POLICY]: 'Permission Policy UUID',
};

const OMNISEARCH_SUPPORTED_RESOURCE_TYPES: ReadonlySet<ResourceType> = new Set([
    ResourceType.ORGANIZATION,
    ResourceType.USER,
    ResourceType.ROLE_ASSIGNMENT,
    ResourceType.CREDENTIAL,
    ResourceType.PROCESSOR,
    ResourceType.MERCHANT_ACCOUNT,
    ResourceType.LOGICAL_TERMINAL,
    ResourceType.TERMINAL_DEVICE,
    ResourceType.PERMISSIONS_BUNDLE,
    ResourceType.PERMISSION_POLICY,
]);

const ORGANIZATION_SCOPED_SOURCE_RESOURCE_TYPES: readonly ResourceType[] = [
    ResourceType.ORGANIZATION,
    ResourceType.USER,
    ResourceType.ROLE_ASSIGNMENT,
    ResourceType.CREDENTIAL,
    ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY,
    ResourceType.IPX_CREDENTIAL_DEFINITION,
    ResourceType.IPX_WEB_RESOURCE_MODEL,
    ResourceType.ALLOWED_WEBSITE_TEMPLATE,
    ResourceType.IPW,
    ResourceType.IPG,
    ResourceType.PROCESSOR,
    ResourceType.MERCHANT_ACCOUNT,
    ResourceType.LOGICAL_TERMINAL,
    ResourceType.TERMINAL_DEVICE,
    ResourceType.PAYMENT_TRANSACTION,
    ResourceType.PAYMENT_REQUEST,
    ResourceType.PAYMENT_METHOD_TOKEN,
    ResourceType.PERMISSION_POLICY,
];

const CURRENT_PERMISSION_TARGET_RESOURCE_TYPES: readonly ResourceType[] = ALL_RESOURCE_TYPES.filter(
    (resourceType) => resourceType !== ResourceType.CREDENTIAL
);

const RPA_MODULE_SEGMENT_LABELS: ReadonlyMap<string, string> = new Map([
    ['cardpointe', 'CardPointe'],
    ['dealertrack', 'Dealertrack'],
    ['generic', 'Generic'],
    ['pcpartpicker', 'PCPartPicker'],
    ['stoneprofits', 'StoneProfits'],
    ['website', 'Website'],
    ['workflows', 'Workflows'],
]);

export function formatRpaModuleLabel(moduleName: RpaModuleName): string {
    return moduleName
        .split('-')
        .map((moduleNameSegment: string) => formatRpaModuleNameSegment(moduleNameSegment))
        .join(' ');
}

function formatRpaModuleNameSegment(moduleNameSegment: string): string {
    // Customer-facing labels never show the internal IPG and IPW terms.
    if (moduleNameSegment === 'ipg') {
        return 'Payment Processor';
    }
    if (moduleNameSegment === 'ipw') {
        return 'Website';
    }

    const knownSegmentLabel: string | undefined = RPA_MODULE_SEGMENT_LABELS.get(moduleNameSegment);
    if (knownSegmentLabel !== undefined) {
        return knownSegmentLabel;
    }

    const payTechSuffix = 'paytech';
    if (moduleNameSegment.endsWith(payTechSuffix)) {
        const prefix = moduleNameSegment.slice(0, -payTechSuffix.length);
        return `${formatTitleCaseSegment(prefix)}PayTech`;
    }

    return formatTitleCaseSegment(moduleNameSegment);
}

function formatTitleCaseSegment(moduleNameSegment: string): string {
    return moduleNameSegment.length === 0
        ? ''
        : `${moduleNameSegment.charAt(0).toUpperCase()}${moduleNameSegment.slice(1)}`;
}

export const AUTHORIZABLE_ACTION_TOOLTIPS: Record<AuthorizableAction, string> = {
    [AuthorizableAction.USE]:
        'Read for backend use only. This does not allow returning the resource to the client.',
    [AuthorizableAction.ROTATE]:
        'Rotate credential material without implying edit, display, or use access.',
    [AuthorizableAction.LIST]:
        'Return identifiers for matching resources. This does not imply USE or DISPLAY.',
    [AuthorizableAction.DISPLAY]:
        'Read and return the resource to the client. This does not imply USE or LIST.',
    [AuthorizableAction.CREATE]:
        'Create a resource of this type within the reachability bounds of the grant.',
    [AuthorizableAction.EDIT]:
        'Edit a resource within the reachability bounds of the grant without implying access to the pre-existing resource.',
    [AuthorizableAction.DELETE]: 'Mark the resource as logically deleted.',
    [AuthorizableAction.GRANT]:
        'Grant permissions to a role assignment or permission policy, limited to permissions the active role assignment already has within the target organization scope.',
    [AuthorizableAction.REVOKE]:
        'Revoke permissions from a role assignment or permission policy, limited to permissions the active role assignment already has within the target organization scope.',
    [AuthorizableAction.TAKE_PAYMENT]:
        'Authorize taking a payment through a merchant account, logical terminal, or terminal device.',
    [AuthorizableAction.VOID_PAYMENT]:
        'Authorize voiding a payment through a merchant account, logical terminal, or terminal device.',
    [AuthorizableAction.REFUND_PAYMENT]:
        'Authorize refunding a payment through a merchant account, logical terminal, or terminal device.',
    [AuthorizableAction.OVERRIDE_FEE]:
        'Authorize removing surcharge or convenience-fee amounts for a specific payment transaction.',
    [AuthorizableAction.IMPERSONATE]:
        'Authorize impersonating the referenced role assignment or user.',
    [AuthorizableAction.AUDIT]: 'Query audit-log events pertaining to the referenced resource.',
};

const EMPTY_ORGANIZATION_SCOPED_PERMISSION_SOURCE_OPTIONS: OrganizationScopedPermissionSourceOptionsResponse =
    {
        enabled_ipg_system_ids: [],
        enabled_ipw_system_ids: [],
    };

function excludeDeletedPermissionBundles(
    bundles: ReadonlyArray<PermissionsBundlePublic>
): ReadonlyArray<PermissionsBundlePublic> {
    return bundles.filter((bundle) => bundle.status !== Status.DELETED);
}

interface PermissionSourceDesignatorCardState {
    card_id: string;
    source_resource_type: ResourceType;
    source_identifier: string;
    source_descendants: boolean;
}

interface PermissionSourceDesignatorEditorState {
    cards: PermissionSourceDesignatorCardState[];
    wildcard_selected: boolean;
    self_selected: boolean;
}

let nextPermissionSourceDesignatorCardId = 0;

function buildIpwOptions(
    enabledIpwSystemIds: readonly IpwSystemId[]
): ReadonlyArray<{ label: string; value: `${IpwSystemId}` }> {
    return enabledIpwSystemIds.map((systemId) => ({
        label: IpwSystemIdToName[systemId],
        value: `${systemId}`,
    }));
}

function buildIpgOptions(
    enabledIpgSystemIds: readonly IpgSystemId[]
): ReadonlyArray<{ label: string; value: `${IpgSystemId}` }> {
    return enabledIpgSystemIds.map((systemId) => ({
        label: IpgSystemIdToName[systemId],
        value: `${systemId}`,
    }));
}

function resolveIpwSystemId(identifier: string): IpwSystemId | null {
    const numericIdentifier = Number(identifier);
    const matchingSystemId = Object.values(IpwSystemId).find(
        (systemId) => systemId === numericIdentifier
    );
    return matchingSystemId ?? null;
}

function resolveIpgSystemId(identifier: string): IpgSystemId | null {
    const numericIdentifier = Number(identifier);
    const matchingSystemId = Object.values(IpgSystemId).find(
        (systemId) => systemId === numericIdentifier
    );
    return matchingSystemId ?? null;
}

function resolveRpaModuleName(identifier: string): RpaModuleName | null {
    const matchingModule = ALL_RPA_MODULE_NAMES.find((moduleName) => moduleName === identifier);
    return matchingModule ?? null;
}

function resolveResourceTypeValue(identifier: string): ResourceType | null {
    const matchingResourceType = ALL_RESOURCE_TYPES.find(
        (resourceType) => resourceType === identifier
    );
    return matchingResourceType ?? null;
}

function buildPermissionSourceDesignatorCardId(): string {
    nextPermissionSourceDesignatorCardId += 1;
    return `permission-source-card-${nextPermissionSourceDesignatorCardId}`;
}

function buildEmptyPermissionSourceDesignatorCardState(): PermissionSourceDesignatorCardState {
    return {
        card_id: buildPermissionSourceDesignatorCardId(),
        source_resource_type: ResourceType.ORGANIZATION,
        source_identifier: '',
        source_descendants: false,
    };
}

function buildPermissionSourceDesignatorCardStateFromResource(
    resource: PermissionSourceDesignator
): PermissionSourceDesignatorCardState | null {
    if (
        resource.designator_type === PermissionSourceDesignatorType.WILDCARD ||
        resource.designator_type === PermissionSourceDesignatorType.SELF
    ) {
        return null;
    }
    switch (resource.designator_type) {
        case ResourceType.ORGANIZATION:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.ORGANIZATION,
                source_identifier: resource.organization_uuid,
                source_descendants: resource.descendants,
            };
        case ResourceType.USER:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.USER,
                source_identifier: resource.user_uuid,
                source_descendants: false,
            };
        case ResourceType.ROLE_ASSIGNMENT:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.ROLE_ASSIGNMENT,
                source_identifier: resource.role_assignment_uuid,
                source_descendants: false,
            };
        case ResourceType.CREDENTIAL:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.CREDENTIAL,
                source_identifier: resource.credential_uuid,
                source_descendants: false,
            };
        case ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY,
                source_identifier: resource.organization_ipx_credential_policy_uuid,
                source_descendants: false,
            };
        case ResourceType.IPX_CREDENTIAL_DEFINITION:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.IPX_CREDENTIAL_DEFINITION,
                source_identifier: resource.ipx_credential_definition_uuid,
                source_descendants: false,
            };
        case ResourceType.IPX_WEB_RESOURCE_MODEL:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.IPX_WEB_RESOURCE_MODEL,
                source_identifier: resource.ipx_web_resource_model_uuid,
                source_descendants: false,
            };
        case ResourceType.ALLOWED_WEBSITE_TEMPLATE:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.ALLOWED_WEBSITE_TEMPLATE,
                source_identifier: resource.allowed_website_template_uuid,
                source_descendants: false,
            };
        case ResourceType.IPW:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.IPW,
                source_identifier: `${resource.ipw_system_id}`,
                source_descendants: false,
            };
        case ResourceType.IPG:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.IPG,
                source_identifier: `${resource.ipg_system_id}`,
                source_descendants: false,
            };
        case ResourceType.PROCESSOR:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PROCESSOR,
                source_identifier: resource.processor_uuid,
                source_descendants: false,
            };
        case ResourceType.MERCHANT_ACCOUNT:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.MERCHANT_ACCOUNT,
                source_identifier: resource.merchant_account_uuid,
                source_descendants: false,
            };
        case ResourceType.LOGICAL_TERMINAL:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.LOGICAL_TERMINAL,
                source_identifier: resource.logical_terminal_uuid,
                source_descendants: false,
            };
        case ResourceType.TERMINAL_DEVICE:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.TERMINAL_DEVICE,
                source_identifier: resource.terminal_device_uuid,
                source_descendants: false,
            };
        case ResourceType.PAYMENT_TRANSACTION:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PAYMENT_TRANSACTION,
                source_identifier: resource.payment_transaction_uuid,
                source_descendants: false,
            };
        case ResourceType.PAYMENT_REQUEST:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PAYMENT_REQUEST,
                source_identifier: resource.payment_request_uuid,
                source_descendants: false,
            };
        case ResourceType.PAYMENT_METHOD_TOKEN:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PAYMENT_METHOD_TOKEN,
                source_identifier: resource.payment_method_token_uuid,
                source_descendants: false,
            };
        case ResourceType.PERMISSIONS_BUNDLE:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PERMISSIONS_BUNDLE,
                source_identifier: resource.permissions_bundle_uuid,
                source_descendants: false,
            };
        case ResourceType.PERMISSION_POLICY:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.PERMISSION_POLICY,
                source_identifier: resource.permission_policy_uuid,
                source_descendants: false,
            };
        case ResourceType.RPA_MODULE:
            return {
                card_id: buildPermissionSourceDesignatorCardId(),
                source_resource_type: ResourceType.RPA_MODULE,
                source_identifier: resource.rpa_module_name,
                source_descendants: false,
            };
    }
}

function buildPermissionSourceDesignatorEditorState(
    resources: readonly PermissionSourceDesignator[]
): PermissionSourceDesignatorEditorState {
    const cards = resources
        .map((resource) => buildPermissionSourceDesignatorCardStateFromResource(resource))
        .filter((card): card is PermissionSourceDesignatorCardState => card !== null);
    const wildcard_selected = resources.some((resource) => {
        return resource.designator_type === PermissionSourceDesignatorType.WILDCARD;
    });
    const self_selected = resources.some((resource) => {
        return resource.designator_type === PermissionSourceDesignatorType.SELF;
    });
    return {
        cards,
        wildcard_selected,
        self_selected,
    };
}

export interface PermissionGrantEditorProps {
    value: PermissionsGrant;
    onChange: (nextValue: PermissionsGrant) => void;
    eligibleActions: readonly AuthorizableAction[];
    organizationUuid?: string | null;
    allowScopeNeutralWildcard?: boolean;
    wildcardSourceLabel?: string;
}

function formatUserSearchLabel(user: UserProfileDetailedPublicResponse): string {
    const fullName = [user.first_name, user.last_name]
        .filter((part): part is string => part !== null && part.trim().length > 0)
        .join(' ');
    return fullName.length > 0 ? `${fullName} (${user.primary_email})` : user.primary_email;
}

export function normalizePermissionResources(
    resources: readonly PermissionSourceDesignator[]
): PermissionSourceDesignator[] {
    const dedupedResources: PermissionSourceDesignator[] = [];
    const seenKeys: Set<string> = new Set();
    for (const resource of resources) {
        const resourceKey = permissionSourceDesignatorKey(resource);
        if (seenKeys.has(resourceKey)) {
            continue;
        }
        seenKeys.add(resourceKey);
        dedupedResources.push(resource);
    }
    return dedupedResources;
}

export function filterPermissionActionsForResourceType(
    resourceType: ResourceType,
    actions: readonly AuthorizableAction[]
): AuthorizableAction[] {
    const allowedActions: readonly AuthorizableAction[] =
        getAllowedActionsForResourceType(resourceType);
    return actions.filter((action) => allowedActions.includes(action));
}

function permissionSourceDesignatorKey(designator: PermissionSourceDesignator): string {
    if (
        designator.designator_type === PermissionSourceDesignatorType.WILDCARD ||
        designator.designator_type === PermissionSourceDesignatorType.SELF
    ) {
        return `designator:${designator.designator_type}`;
    }
    return resourceDesignatorKey(designator);
}

function buildResourceDesignator(
    sourceResourceType: ResourceType,
    identifier: string,
    descendants: boolean,
    organizationUuid: string | null
): PermissionSourceDesignator | null {
    switch (sourceResourceType) {
        case ResourceType.ORGANIZATION: {
            const organizationIdentifier =
                identifier.trim().length > 0 ? identifier.trim() : organizationUuid;
            if (organizationIdentifier === null || !UUID_PATTERN.test(organizationIdentifier)) {
                return null;
            }
            return {
                designator_type: ResourceType.ORGANIZATION,
                organization_uuid: organizationIdentifier,
                descendants,
            };
        }
        case ResourceType.USER:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.USER,
                      user_uuid: identifier,
                  }
                : null;
        case ResourceType.ROLE_ASSIGNMENT:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.ROLE_ASSIGNMENT,
                      role_assignment_uuid: identifier,
                  }
                : null;
        case ResourceType.CREDENTIAL:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.CREDENTIAL,
                      credential_uuid: identifier,
                  }
                : null;
        case ResourceType.ORGANIZATION_CREDENTIAL:
        case ResourceType.ROLE_ASSIGNMENT_CREDENTIAL:
            return null;
        case ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY,
                      organization_ipx_credential_policy_uuid: identifier,
                  }
                : null;
        case ResourceType.IPX_CREDENTIAL_DEFINITION:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.IPX_CREDENTIAL_DEFINITION,
                      ipx_credential_definition_uuid: identifier,
                  }
                : null;
        case ResourceType.IPX_WEB_RESOURCE_MODEL:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.IPX_WEB_RESOURCE_MODEL,
                      ipx_web_resource_model_uuid: identifier,
                  }
                : null;
        case ResourceType.ALLOWED_WEBSITE_TEMPLATE:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.ALLOWED_WEBSITE_TEMPLATE,
                      allowed_website_template_uuid: identifier,
                  }
                : null;
        case ResourceType.IPW: {
            const ipwSystemId = resolveIpwSystemId(identifier);
            return ipwSystemId === null
                ? null
                : {
                      designator_type: ResourceType.IPW,
                      ipw_system_id: ipwSystemId,
                  };
        }
        case ResourceType.IPG: {
            const ipgSystemId = resolveIpgSystemId(identifier);
            return ipgSystemId === null
                ? null
                : {
                      designator_type: ResourceType.IPG,
                      ipg_system_id: ipgSystemId,
                  };
        }
        case ResourceType.PROCESSOR:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PROCESSOR,
                      processor_uuid: identifier,
                  }
                : null;
        case ResourceType.MERCHANT_ACCOUNT:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.MERCHANT_ACCOUNT,
                      merchant_account_uuid: identifier,
                  }
                : null;
        case ResourceType.LOGICAL_TERMINAL:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.LOGICAL_TERMINAL,
                      logical_terminal_uuid: identifier,
                  }
                : null;
        case ResourceType.TERMINAL_DEVICE:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.TERMINAL_DEVICE,
                      terminal_device_uuid: identifier,
                  }
                : null;
        case ResourceType.PAYMENT_TRANSACTION:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PAYMENT_TRANSACTION,
                      payment_transaction_uuid: identifier,
                  }
                : null;
        case ResourceType.PAYMENT_REQUEST:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PAYMENT_REQUEST,
                      payment_request_uuid: identifier,
                  }
                : null;
        case ResourceType.PAYMENT_METHOD_TOKEN:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PAYMENT_METHOD_TOKEN,
                      payment_method_token_uuid: identifier,
                  }
                : null;
        case ResourceType.PERMISSIONS_BUNDLE:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PERMISSIONS_BUNDLE,
                      permissions_bundle_uuid: identifier,
                  }
                : null;
        case ResourceType.PERMISSION_POLICY:
            return UUID_PATTERN.test(identifier)
                ? {
                      designator_type: ResourceType.PERMISSION_POLICY,
                      permission_policy_uuid: identifier,
                  }
                : null;
        case ResourceType.RPA_MODULE: {
            const rpaModuleName = resolveRpaModuleName(identifier);
            return rpaModuleName === null
                ? null
                : {
                      designator_type: ResourceType.RPA_MODULE,
                      rpa_module_name: rpaModuleName,
                  };
        }
    }
}

function getDefaultIdentifierForResourceType(
    sourceResourceType: ResourceType,
    organizationUuid: string | null
): string {
    switch (sourceResourceType) {
        case ResourceType.ORGANIZATION:
            return organizationUuid ?? '';
        case ResourceType.RPA_MODULE:
            return getDefaultRpaModuleName();
        default:
            return '';
    }
}

function getDefaultRpaModuleName(): string {
    return (
        AVAILABLE_RPA_MODULE_NAMES.find((moduleName: RpaModuleName) =>
            moduleName.endsWith('-ipg')
        ) ??
        AVAILABLE_RPA_MODULE_NAMES[0] ??
        ''
    );
}

function buildPermissionSourceDesignatorsFromCards(
    cards: readonly PermissionSourceDesignatorCardState[],
    organizationUuid: string | null
): PermissionSourceDesignator[] {
    return normalizePermissionResources(
        cards
            .map((card) =>
                buildResourceDesignator(
                    card.source_resource_type,
                    card.source_identifier.trim(),
                    card.source_descendants,
                    organizationUuid
                )
            )
            .filter((resource): resource is PermissionSourceDesignator => resource !== null)
    );
}

export function PermissionGrantEditor({
    value,
    onChange,
    eligibleActions,
    organizationUuid = null,
    allowScopeNeutralWildcard = false,
    wildcardSourceLabel,
}: PermissionGrantEditorProps) {
    const initialSourceDesignatorEditorState: PermissionSourceDesignatorEditorState =
        useMemo(() => {
            return buildPermissionSourceDesignatorEditorState(value.resources);
        }, [value.resources]);
    const [organizationScopedSourceOptions, setOrganizationScopedSourceOptions] =
        useState<OrganizationScopedPermissionSourceOptionsResponse>(
            EMPTY_ORGANIZATION_SCOPED_PERMISSION_SOURCE_OPTIONS
        );
    const [organizationScopedSourceOptionsError, setOrganizationScopedSourceOptionsError] =
        useState<string | null>(null);
    const [sourceDesignatorCards, setSourceDesignatorCards] = useState<
        PermissionSourceDesignatorCardState[]
    >(initialSourceDesignatorEditorState.cards);
    const [allResourcesInOrganizationChecked, setAllResourcesInOrganizationChecked] =
        useState<boolean>(initialSourceDesignatorEditorState.wildcard_selected);
    const [selfSourceChecked, setSelfSourceChecked] = useState<boolean>(
        initialSourceDesignatorEditorState.self_selected
    );
    const lastAppliedResourcesSignatureRef = useRef<string>(stringifyLosslessJson(value.resources));
    const resourcesSignature = stringifyLosslessJson(value.resources);
    const canSelectWildcardSource: boolean = organizationUuid !== null || allowScopeNeutralWildcard;

    const availableSourceResourceTypes: readonly ResourceType[] = useMemo(() => {
        if (organizationUuid === null) {
            return ALL_RESOURCE_TYPES;
        }
        return ORGANIZATION_SCOPED_SOURCE_RESOURCE_TYPES;
    }, [organizationUuid]);

    useEffect(() => {
        let isCancelled = false;
        if (organizationUuid === null) {
            setOrganizationScopedSourceOptions(EMPTY_ORGANIZATION_SCOPED_PERMISSION_SOURCE_OPTIONS);
            setOrganizationScopedSourceOptionsError(null);
            return;
        }
        setOrganizationScopedSourceOptionsError(null);
        void permissionsApi
            .getOrganizationScopedPermissionSourceOptions(organizationUuid)
            .then((response) => {
                if (isCancelled) {
                    return;
                }
                setOrganizationScopedSourceOptions(response);
            })
            .catch((error: unknown) => {
                if (isCancelled) {
                    return;
                }
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Failed to load organization-scoped source options.';
                setOrganizationScopedSourceOptions(
                    EMPTY_ORGANIZATION_SCOPED_PERMISSION_SOURCE_OPTIONS
                );
                setOrganizationScopedSourceOptionsError(errorMessage);
            });
        return () => {
            isCancelled = true;
        };
    }, [organizationUuid]);

    const ipwOptions = useMemo(() => {
        return buildIpwOptions(organizationScopedSourceOptions.enabled_ipw_system_ids);
    }, [organizationScopedSourceOptions.enabled_ipw_system_ids]);

    const ipgOptions = useMemo(() => {
        return buildIpgOptions(organizationScopedSourceOptions.enabled_ipg_system_ids);
    }, [organizationScopedSourceOptions.enabled_ipg_system_ids]);

    useEffect(() => {
        if (resourcesSignature === lastAppliedResourcesSignatureRef.current) {
            return;
        }
        const nextEditorState = buildPermissionSourceDesignatorEditorState(value.resources);
        setSourceDesignatorCards(nextEditorState.cards);
        setAllResourcesInOrganizationChecked(nextEditorState.wildcard_selected);
        setSelfSourceChecked(nextEditorState.self_selected);
        lastAppliedResourcesSignatureRef.current = resourcesSignature;
    }, [resourcesSignature, value.resources]);

    const allowedActions: readonly AuthorizableAction[] = useMemo(() => {
        return getAllowedActionsForResourceType(value.resource_type);
    }, [value.resource_type]);

    const unsupportedSelectedActions: AuthorizableAction[] = useMemo(() => {
        return value.actions.filter((action) => !allowedActions.includes(action));
    }, [allowedActions, value.actions]);

    const organizationSearchFetchPage: OmnisearchFetchPage<OrganizationOmnisearchListItem, string> =
        useMemo(() => {
            return async (params) => {
                const response = await organizationApi.searchOrganizations({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: response.organizations,
                    pagination: response.pagination,
                };
            };
        }, [organizationUuid]);

    const userSearchFetchPage: OmnisearchFetchPage<UserProfileDetailedPublicResponse, string> =
        useMemo(() => {
            return async (params) => {
                const response = await userApi.searchUsers({
                    q: params.queryText,
                    ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: response.users,
                    pagination: response.pagination,
                };
            };
        }, [organizationUuid]);

    const roleAssignmentSearchFetchPage: OmnisearchFetchPage<
        PermissionSourceRoleAssignmentListItem,
        string
    > = useMemo(() => {
        return async (params) => {
            if (organizationUuid === null) {
                return {
                    items: [],
                    pagination: { has_more: false },
                };
            }
            const response = await permissionsApi.searchRoleAssignmentsForOrganization(
                organizationUuid,
                {
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                }
            );
            return {
                items: response.role_assignments,
                pagination: response.pagination,
            };
        };
    }, [organizationUuid]);

    const merchantAccountSearchFetchPage: OmnisearchFetchPage<
        MerchantAccountSearchListItem,
        string
    > = useMemo(() => {
        return async (params) => {
            const response = await paymentsInfrastructureApi.searchMerchantAccounts({
                ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                limit: params.limit,
                cursor: params.cursor,
            });
            return {
                items: response.merchant_accounts,
                pagination: response.pagination,
            };
        };
    }, [organizationUuid]);

    const processorSearchFetchPage: OmnisearchFetchPage<
        { processor_uuid: string; processor_name: string },
        string
    > = useMemo(() => {
        return async (params) => {
            const response = await paymentsInfrastructureApi.searchProcessors({
                ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                limit: params.limit,
                cursor: params.cursor,
            });
            return {
                items: response.processors,
                pagination: response.pagination,
            };
        };
    }, [organizationUuid]);

    const credentialSearchFetchPage: OmnisearchFetchPage<SecretListItem, string> = useMemo(() => {
        return async (params) => {
            if (organizationUuid === null) {
                return {
                    items: [],
                    pagination: { has_more: false },
                };
            }
            const response = await secretsApi.listSecrets(
                { organization_uuid: organizationUuid },
                {
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                }
            );
            return {
                items: response.secrets,
                pagination: { has_more: false },
            };
        };
    }, [organizationUuid]);

    const logicalTerminalSearchFetchPage: OmnisearchFetchPage<LogicalTerminalListItem, string> =
        useMemo(() => {
            return async (params) => {
                const response = await paymentsInfrastructureApi.searchLogicalTerminals({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: response.logical_terminals,
                    pagination: response.pagination,
                };
            };
        }, [organizationUuid]);

    const terminalDeviceSearchFetchPage: OmnisearchFetchPage<TerminalDeviceListItem, string> =
        useMemo(() => {
            return async (params) => {
                const response = await paymentsInfrastructureApi.searchTerminalDevices({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    ...(organizationUuid !== null ? { organization_uuid: organizationUuid } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: response.terminal_devices,
                    pagination: response.pagination,
                };
            };
        }, [organizationUuid]);

    const permissionPolicySearchFetchPage: OmnisearchFetchPage<PermissionsBundlePublic, string> =
        useMemo(() => {
            return async (params) => {
                if (organizationUuid === null) {
                    return {
                        items: [],
                        pagination: { has_more: false },
                    };
                }
                const response = await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                    organizationUuid,
                    {
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    }
                );
                return {
                    items: excludeDeletedPermissionBundles(response.bundles),
                    pagination: response.pagination,
                };
            };
        }, [organizationUuid]);

    const sharedBundleSearchFetchPage: OmnisearchFetchPage<PermissionsBundlePublic, string> =
        useMemo(() => {
            return async (params) => {
                const response = await permissionsApi.searchSharedBundles({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: excludeDeletedPermissionBundles(response.bundles),
                    pagination: response.pagination,
                };
            };
        }, []);

    const emitSourceResources = (
        nextCards: readonly PermissionSourceDesignatorCardState[],
        wildcardSelected: boolean,
        selfSelected: boolean
    ): void => {
        const nextResources: PermissionSourceDesignator[] = [
            ...buildPermissionSourceDesignatorsFromCards(nextCards, organizationUuid),
            ...(selfSelected
                ? [{ designator_type: PermissionSourceDesignatorType.SELF } as const]
                : []),
            ...(wildcardSelected
                ? [{ designator_type: PermissionSourceDesignatorType.WILDCARD } as const]
                : []),
        ];
        const nextSignature = stringifyLosslessJson(nextResources);
        lastAppliedResourcesSignatureRef.current = nextSignature;
        onChange({
            ...value,
            resources: nextResources,
        });
    };

    const updateSourceDesignatorCard = (
        cardId: string,
        updater: (card: PermissionSourceDesignatorCardState) => PermissionSourceDesignatorCardState
    ): void => {
        const nextCards = sourceDesignatorCards.map((card) => {
            return card.card_id === cardId ? updater(card) : card;
        });
        setSourceDesignatorCards(nextCards);
        emitSourceResources(nextCards, allResourcesInOrganizationChecked, selfSourceChecked);
    };

    const handleActionToggle = (action: AuthorizableAction, checked: boolean): void => {
        const nextActions: AuthorizableAction[] = checked
            ? [...value.actions, action]
            : value.actions.filter((currentAction) => currentAction !== action);
        onChange({
            ...value,
            actions: nextActions,
        });
    };

    const handleEffectChange = (nextEffect: PermissionEffect): void => {
        onChange({
            ...value,
            effect: nextEffect,
        });
    };

    const handleResourceTypeChange = (nextResourceType: ResourceType): void => {
        onChange({
            ...value,
            resource_type: nextResourceType,
            actions: filterPermissionActionsForResourceType(nextResourceType, value.actions),
        });
    };

    const handleAddResourceCard = (): void => {
        const nextCards = [
            ...sourceDesignatorCards,
            {
                ...buildEmptyPermissionSourceDesignatorCardState(),
                source_identifier: getDefaultIdentifierForResourceType(
                    ResourceType.ORGANIZATION,
                    organizationUuid
                ),
            },
        ];
        setAllResourcesInOrganizationChecked(false);
        setSourceDesignatorCards(nextCards);
        emitSourceResources(nextCards, false, selfSourceChecked);
    };

    const handleWildcardChange = (checked: boolean): void => {
        const nextChecked = checked === true;
        setAllResourcesInOrganizationChecked(nextChecked);
        emitSourceResources(sourceDesignatorCards, nextChecked, selfSourceChecked);
    };

    const handleSelfSourceChange = (checked: boolean): void => {
        const nextChecked = checked === true;
        setSelfSourceChecked(nextChecked);
        emitSourceResources(sourceDesignatorCards, allResourcesInOrganizationChecked, nextChecked);
    };

    const handleRemoveResourceCard = (cardId: string): void => {
        const nextCards = sourceDesignatorCards.filter((card) => {
            return card.card_id !== cardId;
        });
        setSourceDesignatorCards(nextCards);
        emitSourceResources(nextCards, allResourcesInOrganizationChecked, selfSourceChecked);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label htmlFor="permission-effect">Effect</Label>
                <Select
                    value={value.effect}
                    onValueChange={(nextValue) => {
                        if (nextValue === 'allow' || nextValue === 'deny') {
                            handleEffectChange(nextValue);
                        }
                    }}
                >
                    <SelectTrigger id="permission-effect">
                        <SelectValue placeholder="Select effect" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="allow">Allow</SelectItem>
                        <SelectItem value="deny">Deny</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-3">
                <Label>Actions</Label>
                <div className="space-y-2">
                    {allowedActions.map((action) => {
                        const checkboxId = `permission-action-${action}`;
                        const isRoleEligible = eligibleActions.includes(action);
                        return (
                            <div key={action} className="flex items-center space-x-2">
                                <Checkbox
                                    id={checkboxId}
                                    checked={value.actions.includes(action)}
                                    onCheckedChange={(checked) =>
                                        handleActionToggle(action, checked === true)
                                    }
                                    disabled={!isRoleEligible}
                                />
                                <Label htmlFor={checkboxId} className="font-normal">
                                    <span
                                        title={AUTHORIZABLE_ACTION_TOOLTIPS[action]}
                                        className="cursor-help"
                                    >
                                        {action}
                                    </span>
                                    {!isRoleEligible && (
                                        <span className="ml-2 text-xs text-muted-foreground">
                                            Not eligible for this role
                                        </span>
                                    )}
                                </Label>
                            </div>
                        );
                    })}
                </div>
                {unsupportedSelectedActions.length > 0 && (
                    <p className="text-sm text-red-600">
                        This permission contains unsupported stored actions for
                        {` ${RESOURCE_TYPE_LABELS[value.resource_type]}.`}
                    </p>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="permission-resource-type">Resource type</Label>
                <Select
                    value={value.resource_type}
                    onValueChange={(nextValue) => {
                        const resourceType = resolveResourceTypeValue(nextValue);
                        if (resourceType === null) {
                            return;
                        }
                        handleResourceTypeChange(resourceType);
                    }}
                >
                    <SelectTrigger id="permission-resource-type">
                        <SelectValue placeholder="Select resource type" />
                    </SelectTrigger>
                    <SelectContent>
                        {CURRENT_PERMISSION_TARGET_RESOURCE_TYPES.map((resourceType) => (
                            <SelectItem
                                key={resourceType}
                                id={`permission-resource-type-option-${resourceType}`}
                                value={resourceType}
                            >
                                {RESOURCE_TYPE_LABELS[resourceType]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-3">
                <Label>Attached to</Label>
                <div className="space-y-3">
                    <div className="flex justify-end">
                        <Button
                            id="policy-permission-editor-add-source"
                            type="button"
                            variant="outline"
                            onClick={handleAddResourceCard}
                            disabled={allResourcesInOrganizationChecked}
                        >
                            Add resources
                        </Button>
                    </div>

                    {(canSelectWildcardSource || selfSourceChecked) && (
                        <div className="space-y-3 rounded-md border p-4">
                            {canSelectWildcardSource && (
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="permission-source-all-resources"
                                        checked={allResourcesInOrganizationChecked}
                                        onCheckedChange={(checked) =>
                                            handleWildcardChange(checked === true)
                                        }
                                    />
                                    <Label
                                        htmlFor="permission-source-all-resources"
                                        className="font-normal"
                                    >
                                        {wildcardSourceLabel ??
                                            `All ${formatPermissionResourceTypePluralLabel(
                                                value.resource_type
                                            )} in this Organization`}
                                    </Label>
                                </div>
                            )}
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="permission-source-self"
                                    checked={selfSourceChecked}
                                    onCheckedChange={(checked) =>
                                        handleSelfSourceChange(checked === true)
                                    }
                                />
                                <Label htmlFor="permission-source-self" className="font-normal">
                                    Current active role assignment
                                </Label>
                            </div>
                        </div>
                    )}

                    {sourceDesignatorCards.map((card, index) => (
                        <div
                            key={card.card_id}
                            data-permission-source-card-index={index}
                            data-permission-source-selected={
                                card.source_identifier.trim().length > 0 ? 'true' : 'false'
                            }
                            className="grid gap-3 rounded-md border p-4"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="space-y-2 flex-1">
                                    <Label htmlFor={`permission-source-type-${card.card_id}`}>
                                        Source Resource Type
                                    </Label>
                                    <Select
                                        value={card.source_resource_type}
                                        onValueChange={(nextValue) => {
                                            const resourceType =
                                                resolveResourceTypeValue(nextValue);
                                            if (resourceType === null) {
                                                return;
                                            }
                                            updateSourceDesignatorCard(
                                                card.card_id,
                                                (currentCard) => ({
                                                    ...currentCard,
                                                    source_resource_type: resourceType,
                                                    source_identifier:
                                                        getDefaultIdentifierForResourceType(
                                                            resourceType,
                                                            organizationUuid
                                                        ),
                                                    source_descendants: false,
                                                })
                                            );
                                        }}
                                    >
                                        <SelectTrigger
                                            id={`permission-source-type-${card.card_id}`}
                                        >
                                            <SelectValue placeholder="Select source type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableSourceResourceTypes.map((resourceType) => (
                                                <SelectItem
                                                    key={resourceType}
                                                    id={`permission-source-type-${index}-option-${resourceType}`}
                                                    value={resourceType}
                                                >
                                                    {RESOURCE_TYPE_LABELS[resourceType]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600"
                                    onClick={() => handleRemoveResourceCard(card.card_id)}
                                >
                                    [X]
                                </Button>
                            </div>

                            {RESOURCE_IDENTIFIER_LABELS[card.source_resource_type] !== undefined &&
                                (OMNISEARCH_SUPPORTED_RESOURCE_TYPES.has(
                                    card.source_resource_type
                                ) ? (
                                    <>
                                        {card.source_resource_type ===
                                            ResourceType.ORGANIZATION && (
                                            <OmnisearchSelect<
                                                OrganizationOmnisearchListItem,
                                                string
                                            >
                                                label="Organization"
                                                placeholder="Search organizations..."
                                                pageSize={25}
                                                fetchPage={organizationSearchFetchPage}
                                                getItemId={(item) => item.organization_uuid}
                                                getItemLabel={(item) => item.name}
                                                getItemFilterText={(item) =>
                                                    `${item.name} ${item.organization_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.organization_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type === ResourceType.USER && (
                                            <OmnisearchSelect<
                                                UserProfileDetailedPublicResponse,
                                                string
                                            >
                                                label="User"
                                                placeholder="Search users..."
                                                pageSize={25}
                                                fetchPage={userSearchFetchPage}
                                                getItemId={(item) => item.user_uuid}
                                                getItemLabel={formatUserSearchLabel}
                                                getItemFilterText={(item) =>
                                                    buildUserProfileSearchFilterText(item)
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: item.user_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.ROLE_ASSIGNMENT && (
                                            <OmnisearchSelect<
                                                PermissionSourceRoleAssignmentListItem,
                                                string
                                            >
                                                label="Role Assignment"
                                                placeholder="Search role assignments..."
                                                pageSize={25}
                                                fetchPage={roleAssignmentSearchFetchPage}
                                                getItemId={(item) => item.role_assignment_uuid}
                                                getItemLabel={(item) =>
                                                    `${item.user_display_name} (${item.role}) @ ${item.organization_name}`
                                                }
                                                getItemFilterText={(item) =>
                                                    `${item.user_display_name} ${item.primary_email} ${item.role} ${item.organization_name} ${item.role_assignment_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.role_assignment_uuid,
                                                        })
                                                    )
                                                }
                                                disabled={
                                                    organizationUuid === null ||
                                                    organizationScopedSourceOptionsError !== null
                                                }
                                                resetToken={`org=${organizationUuid ?? 'none'}-${index}`}
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.MERCHANT_ACCOUNT && (
                                            <OmnisearchSelect<MerchantAccountSearchListItem, string>
                                                label="Merchant Account"
                                                placeholder="Search merchant accounts..."
                                                pageSize={25}
                                                fetchPage={merchantAccountSearchFetchPage}
                                                getItemId={(item) => item.merchant_account_uuid}
                                                getItemLabel={(item) =>
                                                    `${item.organization_name} / ${item.processor_name} / ${item.processor_mid}`
                                                }
                                                getItemFilterText={(item) =>
                                                    `${item.organization_name} ${item.processor_name} ${item.processor_mid} ${item.merchant_account_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.merchant_account_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type === ResourceType.CREDENTIAL && (
                                            <OmnisearchSelect<SecretListItem, string>
                                                label="Credential"
                                                placeholder="Search credentials..."
                                                pageSize={25}
                                                fetchPage={credentialSearchFetchPage}
                                                getItemId={(item) => item.credential_uuid}
                                                getItemLabel={(item) =>
                                                    `${item.account_id} / ${item.masked_preview}`
                                                }
                                                getItemFilterText={(item) =>
                                                    `${item.account_id} ${item.masked_preview} ${item.credential_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: item.credential_uuid,
                                                        })
                                                    )
                                                }
                                                disabled={organizationUuid === null}
                                                resetToken={`org=${organizationUuid ?? 'none'}-${index}`}
                                            />
                                        )}

                                        {card.source_resource_type === ResourceType.PROCESSOR && (
                                            <OmnisearchSelect<
                                                { processor_uuid: string; processor_name: string },
                                                string
                                            >
                                                label="Processor"
                                                placeholder="Search processors..."
                                                pageSize={25}
                                                fetchPage={processorSearchFetchPage}
                                                getItemId={(item) => item.processor_uuid}
                                                getItemLabel={(item) => item.processor_name}
                                                getItemFilterText={(item) =>
                                                    `${item.processor_name} ${item.processor_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: item.processor_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.LOGICAL_TERMINAL && (
                                            <OmnisearchSelect<LogicalTerminalListItem, string>
                                                label="Logical Terminal"
                                                placeholder="Search logical terminals..."
                                                pageSize={25}
                                                fetchPage={logicalTerminalSearchFetchPage}
                                                getItemId={(item) => item.logical_terminal_uuid}
                                                getItemLabel={getLogicalTerminalDisplayLabel}
                                                getItemFilterText={(item) =>
                                                    `${item.name ?? ''} ${item.processor_name} ${item.processor_mid} ${item.processor_tid} ${item.logical_terminal_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.logical_terminal_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.TERMINAL_DEVICE && (
                                            <OmnisearchSelect<TerminalDeviceListItem, string>
                                                label="Terminal Device"
                                                placeholder="Search terminal devices..."
                                                pageSize={25}
                                                fetchPage={terminalDeviceSearchFetchPage}
                                                getItemId={(item) => item.terminal_device_uuid}
                                                getItemLabel={getTerminalDeviceDisplayLabel}
                                                getItemFilterText={(item) =>
                                                    `${item.name ?? ''} ${item.processor_name} ${item.processor_mid} ${item.processor_tid} ${item.processor_device_id} ${item.terminal_device_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.terminal_device_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.PERMISSION_POLICY && (
                                            <OmnisearchSelect<PermissionsBundlePublic, string>
                                                label="Permission Policy"
                                                placeholder="Search policy bundles..."
                                                pageSize={25}
                                                fetchPage={permissionPolicySearchFetchPage}
                                                getItemId={(item) => item.permissions_bundle_uuid}
                                                getItemLabel={(item) => item.name}
                                                getItemFilterText={(item) =>
                                                    `${item.name} ${item.description ?? ''} ${item.permissions_bundle_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.permissions_bundle_uuid,
                                                        })
                                                    )
                                                }
                                                disabled={organizationUuid === null}
                                                resetToken={`org=${organizationUuid ?? 'none'}-${index}`}
                                            />
                                        )}

                                        {card.source_resource_type ===
                                            ResourceType.PERMISSIONS_BUNDLE && (
                                            <OmnisearchSelect<PermissionsBundlePublic, string>
                                                label="Permissions Bundle"
                                                placeholder="Search shared bundles..."
                                                pageSize={25}
                                                fetchPage={sharedBundleSearchFetchPage}
                                                getItemId={(item) => item.permissions_bundle_uuid}
                                                getItemLabel={(item) => item.name}
                                                getItemFilterText={(item) =>
                                                    `${item.name} ${item.description ?? ''} ${item.permissions_bundle_uuid}`.toLowerCase()
                                                }
                                                selectedItemId={
                                                    card.source_identifier.trim().length > 0
                                                        ? card.source_identifier
                                                        : null
                                                }
                                                onClearSelection={() =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier: '',
                                                        })
                                                    )
                                                }
                                                onSelect={(item) =>
                                                    updateSourceDesignatorCard(
                                                        card.card_id,
                                                        (currentCard) => ({
                                                            ...currentCard,
                                                            source_identifier:
                                                                item.permissions_bundle_uuid,
                                                        })
                                                    )
                                                }
                                            />
                                        )}
                                    </>
                                ) : (
                                    <div className="space-y-2">
                                        <Label
                                            htmlFor={`permission-source-identifier-${card.card_id}`}
                                        >
                                            {RESOURCE_IDENTIFIER_LABELS[card.source_resource_type]}
                                        </Label>
                                        <Input
                                            id={`permission-source-identifier-${card.card_id}`}
                                            value={card.source_identifier}
                                            onChange={(event) =>
                                                updateSourceDesignatorCard(
                                                    card.card_id,
                                                    (currentCard) => ({
                                                        ...currentCard,
                                                        source_identifier: event.target.value,
                                                    })
                                                )
                                            }
                                            placeholder={
                                                RESOURCE_IDENTIFIER_LABELS[
                                                    card.source_resource_type
                                                ]
                                            }
                                        />
                                    </div>
                                ))}

                            {organizationScopedSourceOptionsError !== null &&
                                (card.source_resource_type === ResourceType.ROLE_ASSIGNMENT ||
                                    card.source_resource_type === ResourceType.IPW ||
                                    card.source_resource_type === ResourceType.IPG) && (
                                    <p className="text-sm text-red-600">
                                        {organizationScopedSourceOptionsError}
                                    </p>
                                )}

                            {card.source_resource_type === ResourceType.ORGANIZATION && (
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`permission-source-descendants-${card.card_id}`}
                                        checked={card.source_descendants}
                                        onCheckedChange={(checked) =>
                                            updateSourceDesignatorCard(
                                                card.card_id,
                                                (currentCard) => ({
                                                    ...currentCard,
                                                    source_descendants: checked === true,
                                                })
                                            )
                                        }
                                    />
                                    <Label
                                        htmlFor={`permission-source-descendants-${card.card_id}`}
                                        className="font-normal"
                                    >
                                        Include descendants
                                    </Label>
                                </div>
                            )}

                            {card.source_resource_type === ResourceType.IPW && (
                                <div className="space-y-2">
                                    <Label
                                        htmlFor={`permission-source-ipw-system-id-${card.card_id}`}
                                    >
                                        Website
                                    </Label>
                                    <Select
                                        value={card.source_identifier}
                                        onValueChange={(nextValue) =>
                                            updateSourceDesignatorCard(
                                                card.card_id,
                                                (currentCard) => ({
                                                    ...currentCard,
                                                    source_identifier: nextValue,
                                                })
                                            )
                                        }
                                    >
                                        <SelectTrigger
                                            id={`permission-source-ipw-system-id-${card.card_id}`}
                                        >
                                            <SelectValue placeholder="Select website" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ipwOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {ipwOptions.length === 0 && (
                                        <p className="text-sm text-muted-foreground">
                                            No reachable websites found.
                                        </p>
                                    )}
                                </div>
                            )}

                            {card.source_resource_type === ResourceType.IPG && (
                                <div className="space-y-2">
                                    <Label
                                        htmlFor={`permission-source-ipg-system-id-${card.card_id}`}
                                    >
                                        Payment Processor
                                    </Label>
                                    <Select
                                        value={card.source_identifier}
                                        onValueChange={(nextValue) =>
                                            updateSourceDesignatorCard(
                                                card.card_id,
                                                (currentCard) => ({
                                                    ...currentCard,
                                                    source_identifier: nextValue,
                                                })
                                            )
                                        }
                                    >
                                        <SelectTrigger
                                            id={`permission-source-ipg-system-id-${card.card_id}`}
                                        >
                                            <SelectValue placeholder="Select payment processor" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ipgOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {ipgOptions.length === 0 && (
                                        <p className="text-sm text-muted-foreground">
                                            No reachable payment processors found.
                                        </p>
                                    )}
                                </div>
                            )}

                            {card.source_resource_type === ResourceType.RPA_MODULE && (
                                <div className="space-y-2">
                                    <Label htmlFor={`permission-source-rpa-module-${card.card_id}`}>
                                        RPA Module
                                    </Label>
                                    <Select
                                        value={card.source_identifier}
                                        onValueChange={(nextValue) =>
                                            updateSourceDesignatorCard(
                                                card.card_id,
                                                (currentCard) => ({
                                                    ...currentCard,
                                                    source_identifier: nextValue,
                                                })
                                            )
                                        }
                                    >
                                        <SelectTrigger
                                            id={`permission-source-rpa-module-${card.card_id}`}
                                        >
                                            <SelectValue placeholder="Select RPA module" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {AVAILABLE_RPA_MODULE_NAMES.map((moduleName) => (
                                                <SelectItem key={moduleName} value={moduleName}>
                                                    {formatRpaModuleLabel(moduleName)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    ))}

                    {sourceDesignatorCards.length === 0 && allResourcesInOrganizationChecked && (
                        <div className="rounded-md border p-3 text-sm">
                            {formatPermissionDesignatorLabel(
                                {
                                    designator_type: PermissionSourceDesignatorType.WILDCARD,
                                },
                                value.resource_type
                            )}
                        </div>
                    )}
                    {sourceDesignatorCards.length === 0 && selfSourceChecked && (
                        <div className="rounded-md border p-3 text-sm">
                            {formatPermissionDesignatorLabel({
                                designator_type: PermissionSourceDesignatorType.SELF,
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
